import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { FadeIn, SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";

const themes = [
  {
    n: "01",
    title: "Communicating without escalating conflict",
    text: "When emotions are raw, even practical conversations can turn painful. We work on slowing exchanges down, lowering the temperature, and saying difficult things in ways that can actually be heard.",
  },
  {
    n: "02",
    title: "Processing the emotions and grief",
    text: "The end of a marriage is a real loss — of the relationship, the routines, and the future you imagined. Counseling gives that grief a place to go, so it doesn't have to leak into every conversation or decision.",
  },
  {
    n: "03",
    title: "Establishing healthy boundaries",
    text: "Transitioning from spouses to separate individuals requires new boundaries — around communication, space, family and daily life. We define them clearly and practice holding them with respect.",
  },
  {
    n: "04",
    title: "Navigating the difficult conversations",
    text: "Assets, property, finances and shared responsibilities like co-parenting all require discussions most couples have never had. Therapy helps you approach them with structure and less damage — alongside, not instead of, any legal guidance.",
  },
];

const Separation = () => {
  usePageMeta(
    "Separation & Post-Marital Counseling in California | Holistic Healing Psychotherapy",
    "Compassionate counseling for separation, divorce and life after marriage: communicating without escalating conflict, processing grief, healthy boundaries and co-parenting conversations. California LCSW, telehealth statewide."
  );

  return (
    <div data-testid="separation-page">
      <section className="bg-forest-950 pt-16 pb-20 lg:pt-28 lg:pb-28 relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-[480px] h-[480px] rounded-full bg-gold-500/8 blur-3xl pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 sm:px-8 relative">
          <FadeIn>
            <p className="eyebrow mb-6" data-testid="separation-eyebrow">Separation & Post-Marital Counseling</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-cream-50" data-testid="separation-headline">
              When a marriage ends, how it ends <em className="text-gold-400">matters</em>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-cream-50/70 leading-relaxed max-w-2xl" data-testid="separation-intro">
              Whether you are considering separation, in the middle of it, or finding your footing after it, this is
              one of life's hardest transitions. Counseling offers a structured, nonjudgmental place to move through
              it with clarity — and, when children or shared responsibilities remain, with as much care as possible.
            </p>
            <Link
              to="/contact"
              data-testid="separation-schedule-cta"
              className="inline-flex items-center gap-2 mt-9 rounded-full bg-cream-50 text-forest-900 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-white"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="bg-cream-50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading eyebrow="What We Work On" title="Ending with clarity — and as much peace as possible." />
          <div className="mt-14 grid md:grid-cols-2 gap-x-16">
            {themes.map((t, i) => (
              <FadeIn key={t.n} delay={i * 0.08}>
                <div className="group py-8 border-t border-forest-800/10 hover:border-gold-500/50 transition-colors duration-500" data-testid={`separation-theme-${t.n}`}>
                  <div className="flex gap-6">
                    <span className="mt-3 w-3 h-3 rotate-45 bg-gold-500/80 shrink-0" aria-hidden="true" />
                    <div>
                      <h3 className="font-serif text-2xl text-forest-800 mb-3">{t.title}</h3>
                      <p className="text-base text-sage leading-relaxed">{t.text}</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream-100 py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <FadeIn>
            <p className="font-serif text-2xl sm:text-3xl lg:text-4xl text-forest-800 leading-snug" data-testid="separation-close">
              Separation counseling is not about assigning blame. It's about helping two people close one chapter{" "}
              <em className="text-mauve-500">with dignity</em> — and begin the next one whole.
            </p>
            <p className="mt-6 text-sm text-sage max-w-xl mx-auto leading-relaxed" data-testid="separation-note">
              Therapy supports the emotional and communication side of this transition and works well alongside
              mediators and attorneys — it is not a substitute for legal advice.
            </p>
            <Link
              to="/contact"
              data-testid="separation-bottom-cta"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-forest-500 text-cream-50 font-semibold text-sm px-7 py-4 transition-colors duration-300 hover:bg-forest-700"
            >
              Schedule a Consultation <ArrowRight size={16} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <CTASection
        title="You don't have to navigate this alone."
        copy="Schedule a free 20-minute consultation to talk about where you are and what support could look like."
      />
    </div>
  );
};

export default Separation;
